/**
 * Conversation Files Popup
 * 
 * Displays all files attached during a conversation (from widget users and experts).
 * Allows downloading, viewing content, and managing files.
 */

import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useToast } from '../../Context/ToastContext';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const PopupContainer = styled.div`
  background: linear-gradient(135deg, #1a1f2e 0%, #0f1419 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  width: 550px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: ${fadeIn} 0.2s ease-out;
  overflow: hidden;
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #fff;
  }
  
  span {
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.3);
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    color: #a5b4fc;
  }
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.3);
    color: #ef4444;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const PopupContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 3px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.6);
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(99, 102, 241, 0.2);
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    opacity: 0.3;
  }
  
  h4 {
    margin: 0 0 8px 0;
    font-size: 16px;
    color: rgba(255, 255, 255, 0.7);
  }
  
  p {
    margin: 0;
    font-size: 13px;
  }
`;

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: rgba(30, 35, 50, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 12px;
  transition: all 0.2s ease;
  animation: ${slideIn} 0.3s ease-out;
  animation-delay: ${props => props.$index * 0.05}s;
  animation-fill-mode: both;
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.3);
    transform: translateX(4px);
  }
`;

const FileIconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${props => getFileColor(props.$type)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  span {
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    text-transform: uppercase;
  }
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  
  .name {
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 4px;
  }
  
  .meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    
    .source {
      display: flex;
      align-items: center;
      gap: 4px;
      
      &::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${props => props.$source === 'widget-user' ? '#10b981' : '#6366f1'};
      }
    }
  }
`;

const FileActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  background: rgba(99, 102, 241, 0.1);
  color: #a5b4fc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.4);
    color: #fff;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ViewContentButton = styled(ActionButton)`
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.2);
  color: #34d399;
  
  &:hover {
    background: rgba(16, 185, 129, 0.2);
    border-color: rgba(16, 185, 129, 0.4);
  }
`;

// Content Preview Modal
const ContentModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, #1a1f2e 0%, #0f1419 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  width: 700px;
  max-width: 90vw;
  max-height: 80vh;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  
  h4 {
    margin: 0;
    font-size: 15px;
    color: #fff;
  }
`;

const ContentBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  pre {
    margin: 0;
    font-family: 'Fira Code', monospace;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.6;
  }
`;

// Helper function to get file type color
function getFileColor(type) {
  const colors = {
    pdf: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    doc: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    docx: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    xls: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    xlsx: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    png: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    jpg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    jpeg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    txt: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    default: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
  };
  return colors[type?.toLowerCase()] || colors.default;
}

// Component
const ConversationFilesPopup = ({ threadId, userName, onClose }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  const { error: showError } = useToast();
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [viewingContent, setViewingContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  // Fetch files on mount
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setIsLoading(true);
        const response = await authenticatedAxios.get(`/conversation-files/${threadId}`);
        if (response.data.success) {
          setFiles(response.data.files || []);
        } else {
          setError(response.data.error || 'Failed to load files');
        }
      } catch (err) {
        console.error('Error fetching files:', err);
        setError('Failed to load files');
      } finally {
        setIsLoading(false);
      }
    };

    if (threadId) {
      fetchFiles();
    }
  }, [threadId, authenticatedAxios]);

  // Handle download
  const handleDownload = async (file) => {
    try {
      setDownloadingId(file.id);
      
      // If we already have a download URL (expert uploads), use it directly
      if (file.downloadUrl) {
        window.open(file.downloadUrl, '_blank');
        setDownloadingId(null);
        return;
      }
      
      // Get the file key - try multiple possible properties
      const fileKey = file.fileName || file.fileKey || file.key || file.s3Key;
      
      if (!fileKey) {
        console.error('No file key found for file:', file);
        showError('Cannot download: file key not found');
        setDownloadingId(null);
        return;
      }
      
      // Otherwise, request a signed URL from the server
      const response = await authenticatedAxios.post('/conversation-files/download', {
        fileKey: fileKey,
        existingUrl: file.downloadUrl
      });
      
      if (response.data.success && response.data.downloadUrl) {
        // Open download URL in new tab
        window.open(response.data.downloadUrl, '_blank');
      } else {
        showError('Failed to generate download link');
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      const errorMsg = err.response?.data?.error || 'Failed to download file';
      showError(errorMsg);
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle view content
  const handleViewContent = async (file) => {
    if (!file.hasContent) return;
    
    try {
      setContentLoading(true);
      const response = await authenticatedAxios.get(`/conversation-files/content/${file.id}`);
      
      if (response.data.success) {
        setViewingContent({
          fileName: response.data.fileName,
          content: response.data.content
        });
      }
    } catch (err) {
      console.error('Error viewing content:', err);
      showError('Failed to load file content');
    } finally {
      setContentLoading(false);
    }
  };

  // Get file extension for display
  const getFileExtension = (filename) => {
    const parts = filename?.split('.') || [];
    return parts.length > 1 ? parts[parts.length - 1] : '?';
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Get source label
  const getSourceLabel = (source) => {
    if (source === 'widget-user') return 'User uploaded';
    if (source === 'expert' || source === 'expert-upload') return 'Expert shared';
    return 'Uploaded';
  };

  return (
    <PopupOverlay onClick={onClose}>
      <PopupContainer onClick={e => e.stopPropagation()}>
        <PopupHeader>
          <HeaderTitle>
            <h3>📁 Conversation Files</h3>
            {files.length > 0 && <span>{files.length} files</span>}
          </HeaderTitle>
          <CloseButton onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </CloseButton>
        </PopupHeader>

        <PopupContent>
          {isLoading ? (
            <LoadingState>
              <div className="spinner" />
              <span>Loading files...</span>
            </LoadingState>
          ) : error ? (
            <EmptyState>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h4>Error Loading Files</h4>
              <p>{error}</p>
            </EmptyState>
          ) : files.length === 0 ? (
            <EmptyState>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h4>No Files Found</h4>
              <p>No files have been shared in this conversation yet.</p>
            </EmptyState>
          ) : (
            <FilesList>
              {files.map((file, index) => (
                <FileItem key={file.id} $index={index}>
                  <FileIconWrapper $type={file.fileType || getFileExtension(file.originalName)}>
                    <span>{(file.fileType || getFileExtension(file.originalName)).substring(0, 4)}</span>
                  </FileIconWrapper>
                  
                  <FileInfo $source={file.source}>
                    <div className="name" title={file.originalName}>
                      {file.originalName}
                    </div>
                    <div className="meta">
                      <span className="source">{getSourceLabel(file.source)}</span>
                      <span>{formatDate(file.uploadedAt)}</span>
                    </div>
                  </FileInfo>
                  
                  <FileActions>
                    {file.hasContent && (
                      <ViewContentButton 
                        onClick={() => handleViewContent(file)}
                        title="View extracted text"
                        disabled={contentLoading}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </ViewContentButton>
                    )}
                    <ActionButton 
                      onClick={() => handleDownload(file)}
                      title="Download file"
                      disabled={downloadingId === file.id}
                    >
                      {downloadingId === file.id ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                          <path d="M12 2a10 10 0 0 1 10 10" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                        </svg>
                      )}
                    </ActionButton>
                  </FileActions>
                </FileItem>
              ))}
            </FilesList>
          )}
        </PopupContent>
      </PopupContainer>

      {/* Content Preview Modal */}
      {viewingContent && (
        <ContentModal onClick={e => e.stopPropagation()}>
          <ContentHeader>
            <h4>📄 {viewingContent.fileName}</h4>
            <CloseButton onClick={() => setViewingContent(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </CloseButton>
          </ContentHeader>
          <ContentBody>
            <pre>{viewingContent.content}</pre>
          </ContentBody>
        </ContentModal>
      )}
    </PopupOverlay>
  );
};

export default ConversationFilesPopup;

